import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Announcements,
  DragEndEvent,
  DragStartEvent,
  ScreenReaderInstructions,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useMemo, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { BoardColumn as BoardColumnDto, BoardDto, BoardQuery, Lane, TaskDto } from '@botguide/shared';
import { LANES, LANE_LABELS, laneTotals } from '@botguide/shared';

import { NO_VIEW, isNarrowed, narrowGroups, type BoardView } from '@/lib/board-view';

import {
  Button,
  EmptyState,
  ErrorNote,
  ErrorState,
  LoadingScreen,
  PageHeader,
  Spinner,
  TabPanel,
  Tabs,
} from '@/components';
import { BoardColumn, toLaneGroups } from '@/components/board/BoardColumn';
import type { BoardGroup } from '@/components/board/BoardColumn';
import {
  BoardFilters,
  boardFiltersToParams,
  hasActiveFilters,
  parseBoardFilters,
} from '@/components/board/BoardFilters';
import { TaskCardFace } from '@/components/board/TaskCard';
import { TaskDialog } from '@/components/board/TaskDialog';
import { queryKeys, useBoard, useMoveTask } from '@/lib/queries';
import { useCan } from '@/lib/session';

/*
 * ---------------------------------------------------------------------------
 * Why four columns and not ten
 * ---------------------------------------------------------------------------
 *
 * `GET /api/board` returns ten columns: Backlog, Next Up, the seven SDLC
 * stages, and Completed. Ten side by side on a 1280px laptop is roughly 95px
 * each once the rail and the gutters are paid for, which is narrower than a
 * story-point pill and a name. Anything that does not fit gets a horizontal
 * scrollbar, and a board you have to scroll sideways to read is a board nobody
 * reads: the whole value of this screen is seeing where the work actually is
 * in one look.
 *
 * So the top level is the four lanes - the four words the business actually
 * uses - and the stages become labelled groups stacked inside their lane. That
 * keeps every stage a real drop target and a real heading, while the eye still
 * reads left-to-right as backlog -> shipped. Completed is split the same way
 * into Pilot and Production, because `orderIndex` is dense within (lane,
 * stage) and a merged Completed column has no single position a drop could
 * name.
 *
 * Below 1280px the four lanes do not fit either, so the layout changes rather
 * than shrinking: a tab strip switches between lanes and one lane fills the
 * width. On a phone that is the only honest option - four 80px columns are not
 * a board, they are a diagram of one. The cost is that a drag cannot cross
 * lanes while only one is on screen, so the task dialog carries lane and stage
 * selects and is the way work moves on a phone.
 */

/** The width at which four lanes are each wide enough to read a card in. */
const WIDE_BOARD = '(min-width: 1280px)';

const SCREEN_READER_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'To move a task, press space or enter on its move button. While dragging, use the arrow keys to move between stages and positions. Press space or enter again to drop, or escape to cancel.',
};

export default function WorkBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseBoardFilters(searchParams), [searchParams]);

  const board = useBoard(filters);
  const moveTask = useMoveTask();
  const queryClient = useQueryClient();

  const canMove = useCan('move', 'task');
  const canCreate = useCan('create', 'task');

  const wide = useMediaQuery(WIDE_BOARD);
  const tabsId = useId();

  const [lane, setLane] = useState<Lane>('in_progress');
  const [dialog, setDialog] = useState<{ task: TaskDto | null } | null>(null);
  const [dragging, setDragging] = useState<UniqueIdentifier | null>(null);
  const [moveError, setMoveError] = useState<unknown>(null);
  /** The last completed move, announced politely for screen readers. */
  const [announcement, setAnnouncement] = useState('');

  /*
   * Search and the completed-stage filter narrow what is already here. They
   * are not in the URL and not in `BoardQuery` - see `lib/board-view.ts`.
   */
  const [view, setView] = useState<BoardView>(NO_VIEW);

  const laneGroups = useMemo(() => {
    const all = board.data ? toLaneGroups(board.data) : [];
    return all.map((entry) => ({ ...entry, groups: narrowGroups(entry.groups, view) }));
  }, [board.data, view]);

  const groups = useMemo(() => laneGroups.flatMap((entry) => entry.groups), [laneGroups]);

  /*
   * Recount while narrowed rather than trusting the server's totals.
   *
   * `board.totals` counts everything the query fetched, which is the right
   * number until a search hides most of it - and a lane header reading 12
   * above two visible cards is the screen contradicting itself.
   */
  const totals = useMemo(() => {
    const source = isNarrowed(view)
      ? laneTotals(groups.flatMap((group) => group.tasks))
      : (board.data?.totals ?? []);
    return new Map(source.map((total) => [total.lane, total]));
  }, [board.data, groups, view]);

  const draggedTask = useMemo(
    () => (dragging ? (groups.flatMap((group) => group.tasks).find((t) => t.id === dragging) ?? null) : null),
    [dragging, groups],
  );

  const setFilters = useCallback(
    (next: BoardQuery) => {
      // `replace` keeps the back button pointing at the previous screen rather
      // than at every intermediate state of the filter bar.
      setSearchParams(boardFiltersToParams(next), { replace: true });
    },
    [setSearchParams],
  );

  const sensors = useSensors(
    // A short distance rather than an instant grab, so clicking a card to open
    // it does not register as a one-pixel drag.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // On touch a drag has to be deliberate, or every attempt to scroll the
    // column picks a card up instead.
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart: ({ active }) => {
        const task = findTask(groups, active.id);
        return task ? `Picked up ${task.title}.` : undefined;
      },
      onDragOver: ({ active, over }) => {
        const task = findTask(groups, active.id);
        const target = over ? resolveGroup(groups, over.id) : null;
        if (!task || !target) return undefined;
        return `${task.title} is over ${LANE_LABELS[target.lane]}, ${target.label}.`;
      },
      onDragEnd: ({ active, over }) => {
        const task = findTask(groups, active.id);
        const target = over ? resolveGroup(groups, over.id) : null;
        if (!task) return undefined;
        if (!target) return `${task.title} was returned to where it started.`;
        return `${task.title} was dropped into ${LANE_LABELS[target.lane]}, ${target.label}.`;
      },
      onDragCancel: ({ active }) => {
        const task = findTask(groups, active.id);
        return task ? `Move cancelled. ${task.title} stayed where it was.` : undefined;
      },
    }),
    [groups],
  );

  function handleDragEnd(event: DragEndEvent) {
    setDragging(null);

    const { active, over } = event;
    if (!over) return;

    const source = groups.find((group) => group.tasks.some((task) => task.id === active.id));
    const target = resolveGroup(groups, over.id);
    const task = source?.tasks.find((candidate) => candidate.id === active.id);
    if (!source || !target || !task) return;

    const position = dropPosition(event, source, target, task);
    if (position === null) return;

    commitMove(task, target, position);
  }

  /**
   * Move the card now, tell the server, and put it back if the server says no.
   *
   * The optimistic write goes into the query cache rather than into component
   * state, so the board the mutation's own invalidation refetches into is the
   * same object - there is no second source of truth to fall out of step, and
   * the refetch simply replaces the guess with the answer.
   */
  function commitMove(task: TaskDto, target: BoardGroup, position: number) {
    const key = queryKeys.board.view(filters);
    const previous = queryClient.getQueryData<BoardDto>(key);

    if (previous) {
      // An in-flight read would otherwise land on top of the optimistic write.
      void queryClient.cancelQueries({ queryKey: queryKeys.board.all });
      queryClient.setQueryData<BoardDto>(key, applyMove(previous, task.id, target, position));
    }

    setMoveError(null);
    setAnnouncement(`${task.title} moved to ${LANE_LABELS[target.lane]}, ${target.label}.`);

    moveTask.mutate(
      {
        id: task.id,
        move: {
          lane: target.lane,
          /*
           * Whatever stage the group the card landed on carries - which is
           * null for Backlog and Next Up, exactly what the API's own
           * `defaultStageForLane` would fill in. This screen never computes a
           * default stage of its own.
           */
          stage: target.stage,
          position,
        },
      },
      {
        onError: (error) => {
          if (previous) queryClient.setQueryData<BoardDto>(key, previous);
          // The rollback is instant; the refetch confirms it against the server
          // in case something else changed while the move was in flight.
          void queryClient.invalidateQueries({ queryKey: queryKeys.board.all });
          setMoveError(error);
          setAnnouncement(`${task.title} could not be moved and has been put back.`);
        },
      },
    );
  }

  /* ---------------------------------------------------------------------- */

  if (board.isPending) {
    return (
      <>
        <BoardHeader filters={filters} onFilters={setFilters} view={view} onViewChange={setView} />
        <LoadingScreen label="Loading the board" />
      </>
    );
  }

  if (board.isError) {
    return (
      <>
        <BoardHeader filters={filters} onFilters={setFilters} view={view} onViewChange={setView} />
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      </>
    );
  }

  const isEmpty = groups.every((group) => group.tasks.length === 0);
  const filtered = hasActiveFilters(filters);

  return (
    <>
      <BoardHeader
        filters={filters}
        onFilters={setFilters}
        view={view}
        onViewChange={setView}
        onCreate={canCreate ? () => setDialog({ task: null }) : undefined}
        fetching={board.isFetching}
      />

      {moveError ? <ErrorNote error={moveError} className="mb-4" /> : null}

      {/* Moves are announced here as well as through dnd-kit, so a failure
          that happens after the drag has ended is still spoken. */}
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {isEmpty ? (
        <EmptyState
          title={filtered ? 'No tasks match these filters' : 'No work on the board yet'}
          description={
            filtered
              ? 'Widen the filters, or clear them to see everything again.'
              : 'Tasks hang off features, which are created on the Work items screen.'
          }
          action={
            filtered ? (
              <Button size="sm" variant="secondary" iconLeft="close" onClick={() => setFilters({})}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          accessibility={{ announcements, screenReaderInstructions: SCREEN_READER_INSTRUCTIONS }}
          // Columns change height as cards move between them, so droppable
          // rectangles have to be re-measured rather than cached from the start.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={(event: DragStartEvent) => setDragging(event.active.id)}
          onDragCancel={() => setDragging(null)}
          onDragEnd={handleDragEnd}
        >
          {wide ? (
            <div
              className="grid items-start gap-4"
              style={{
                // In Progress carries seven groups, so it earns the extra width.
                gridTemplateColumns:
                  'minmax(0,1fr) minmax(0,1fr) minmax(0,1.35fr) minmax(0,1fr)',
              }}
            >
              {laneGroups.map((entry) => (
                <BoardColumn
                  key={entry.lane}
                  lane={entry.lane}
                  groups={entry.groups}
                  total={totals.get(entry.lane)}
                  canMove={canMove}
                  onOpenTask={(task) => setDialog({ task })}
                  scrolls
                />
              ))}
            </div>
          ) : (
            <>
              <Tabs
                items={LANES.map((value) => ({
                  value,
                  label: LANE_LABELS[value],
                  count: totals.get(value)?.taskCount ?? 0,
                }))}
                value={lane}
                onChange={setLane}
                aria-label="Lane"
                idPrefix={tabsId}
                className="mb-4"
              />
              {laneGroups.map((entry) => (
                <TabPanel key={entry.lane} id={tabsId} value={entry.lane} selected={entry.lane === lane}>
                  <BoardColumn
                    lane={entry.lane}
                    groups={entry.groups}
                    total={totals.get(entry.lane)}
                    canMove={canMove}
                    onOpenTask={(task) => setDialog({ task })}
                  />
                </TabPanel>
              ))}
            </>
          )}

          <DragOverlay dropAnimation={null}>
            {draggedTask ? (
              <div className="w-[17rem] max-w-[80vw]">
                <TaskCardFace task={draggedTask} lifted />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {dialog ? (
        <TaskDialog open task={dialog.task} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

function BoardHeader({
  filters,
  onFilters,
  view,
  onViewChange,
  onCreate,
  fetching = false,
}: {
  filters: BoardQuery;
  onFilters: (next: BoardQuery) => void;
  view: BoardView;
  onViewChange: (next: BoardView) => void;
  /** Omitted while the board is loading or broken - there is nothing to add to. */
  onCreate?: () => void;
  fetching?: boolean;
}) {
  return (
    <PageHeader
      title={
        <span className="inline-flex items-center gap-3">
          Board
          {fetching ? <Spinner size={15} label="Refreshing" /> : null}
        </span>
      }
      description="Every task across BotGuide, in four lanes, with the seven SDLC stages inside In Progress."
      toolbar={
        <BoardFilters value={filters} onChange={onFilters} view={view} onViewChange={onViewChange} />
      }
      actions={
        onCreate ? (
          <Button variant="primary" iconLeft="plus" onClick={onCreate}>
            New task
          </Button>
        ) : undefined
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Drop maths                                                                 */
/* -------------------------------------------------------------------------- */

function findTask(groups: readonly BoardGroup[], id: UniqueIdentifier): TaskDto | null {
  for (const group of groups) {
    const task = group.tasks.find((candidate) => candidate.id === id);
    if (task) return task;
  }
  return null;
}

/** `over.id` is either a group's own id or the id of a card inside one. */
function resolveGroup(groups: readonly BoardGroup[], id: UniqueIdentifier): BoardGroup | null {
  return (
    groups.find((group) => group.id === id) ??
    groups.find((group) => group.tasks.some((task) => task.id === id)) ??
    null
  );
}

/**
 * Where the card was dropped, as the API counts positions.
 *
 * `POST /tasks/:id/move` renumbers within `(client, lane, stage)` - a column
 * belongs to one client's board - so `position` has to be an index into that
 * client's own cards. This board renders every client's work in one column, so
 * the index the eye sees is a different number, and sending it would push a
 * card past cards it was never above. The drop point is worked out visually
 * and then converted by counting only the cards that share a client with it.
 *
 * `null` means nothing actually moved, so there is no request to make.
 */
function dropPosition(
  event: DragEndEvent,
  source: BoardGroup,
  target: BoardGroup,
  task: TaskDto,
): number | null {
  const { active, over } = event;
  if (!over) return null;

  const overIndex = target.tasks.findIndex((candidate) => candidate.id === over.id);
  // The list the card is being inserted into never contains the card itself.
  const destination = target.tasks.filter((candidate) => candidate.id !== active.id);

  let visual: number;

  if (source.id === target.id) {
    const from = source.tasks.findIndex((candidate) => candidate.id === active.id);
    if (overIndex === from) return null;
    visual = overIndex === -1 ? destination.length : overIndex;
  } else if (overIndex === -1) {
    visual = destination.length;
  } else {
    // Above or below the card it was dropped on, by which half it landed in.
    const moving = active.rect.current.translated;
    const overRect = over.rect;
    const below =
      moving !== null && moving.top + moving.height / 2 > overRect.top + overRect.height / 2;
    visual = overIndex + (below ? 1 : 0);
  }

  return destination.slice(0, visual).filter((candidate) => candidate.clientId === task.clientId)
    .length;
}

/**
 * One column after a move, ordered the way the board query returns it.
 *
 * Only the moved card's own client renumbers - that is the scope the move
 * route renumbers in - and the column is then re-read in `(orderIndex,
 * createdAt)` order, which is what `GET /board` sorts by. `insertAt` is null
 * for the column the card left.
 */
function renumbered(tasks: TaskDto[], moved: TaskDto, insertAt: number | null): TaskDto[] {
  const peers = tasks.filter((task) => task.clientId === moved.clientId);
  const others = tasks.filter((task) => task.clientId !== moved.clientId);

  if (insertAt !== null) peers.splice(Math.min(Math.max(insertAt, 0), peers.length), 0, moved);

  const dense = peers.map((task, index) => ({ ...task, orderIndex: index }));

  return [...others, ...dense].sort(
    (a, b) => a.orderIndex - b.orderIndex || a.createdAt.localeCompare(b.createdAt),
  );
}

/**
 * The board with one card moved, for the optimistic write.
 *
 * This mirrors what the move route does rather than just splicing the card in
 * where the cursor was: the destination renumbers that client's cards around
 * the new one, the vacated column closes up behind it, and each column is then
 * read back in `(orderIndex, createdAt)` order - which is how the board query
 * returns it. Guessing any other order means the card visibly hops a row when
 * the refetch lands, and a card that moves twice is a card nobody trusts.
 *
 * The API's column list, its order and its labels are kept exactly as they
 * arrived - only the tasks inside them are refilled - so the browser never
 * invents a column the server did not send.
 */
function applyMove(
  board: BoardDto,
  taskId: string,
  target: BoardGroup,
  position: number,
): BoardDto {
  const groups = toLaneGroups(board).flatMap((entry) => entry.groups);
  const moving = findTask(groups, taskId);
  if (!moving) return board;

  const moved: TaskDto = { ...moving, lane: target.lane, stage: target.stage };

  const next = groups.map((group) => {
    const without = group.tasks.filter((task) => task.id !== taskId);
    const insert = group.id === target.id ? position : null;
    if (insert === null && without.length === group.tasks.length) return group;
    return { ...group, tasks: renumbered(without, moved, insert) };
  });

  const all = next.flatMap((group) => group.tasks);

  return {
    columns: board.columns.map(
      (column): BoardColumnDto => ({
        ...column,
        tasks: next
          .filter(
            (group) =>
              group.lane === column.lane &&
              (column.stage === null || group.stage === column.stage),
          )
          .flatMap((group) => group.tasks),
      }),
    ),
    // Counts and points come from the shared roll-up, the same one the API
    // used, so an optimistic total can never disagree about the arithmetic.
    totals: laneTotals(all),
  };
}

/* -------------------------------------------------------------------------- */

/**
 * A media query as state.
 *
 * The board's layout is not a style change but a structural one - four columns
 * or a tab strip - so the breakpoint has to be readable from JavaScript rather
 * than only from CSS.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
